import { lazy, Suspense, useEffect, useRef, useState } from 'react'
import { Routes, Route, Navigate, useLocation, useParams, useNavigate } from 'react-router-dom'
import { Capacitor } from '@capacitor/core'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import { screenVariants, fadeVariants, reducedVariants } from './config/transitions'
import { useUserStore, useProfileStore, useCaseStore } from './stores'
import { purgeLegacyAuth } from './services/api'
import { useNotificationStore } from './stores/useNotificationStore'
import { MobileLayout, AuthLayout } from './components/layout'
import SessionLockGuard from './components/SessionLockGuard'
import OrellaLoader, { OrellaSpinner } from './components/OrellaLoader'
import ErrorBoundary from './components/ErrorBoundary'
import GlobalAgentProvider from './components/GlobalAgentProvider'
import useQuestionnaireStore from './stores/useQuestionnaireStore'
import { useI18n } from './i18n'

// Global scroll container ref - shared across components
export const scrollContainerRef = { current: null }

// v237.15.0: Navigation direction collapsed to the canonical model — every
// screen opens top-down; only lateral bottom-tab peers fade. The old route
// hierarchy table (used for forward/back horizontal slides) is no longer needed.

const mainTabs = ['/home', '/cases', '/visits', '/profile', '/settings']

function getNavigationDirection(prevPath, nextPath) {
  const prevIsTab = mainTabs.includes(prevPath)
  const nextIsTab = mainTabs.includes(nextPath)
  // Lateral move between bottom-tab peers → fade (a sideways move isn't an "opening").
  if (prevIsTab && nextIsTab && prevPath !== nextPath) return 'fade'
  // Everything else opens top-down (canonical).
  return 'drop'
}

// Auth screens
import WelcomeScreen from './pages/WelcomeScreen'
import SignUpScreen from './pages/auth/SignUpScreen'
import SignInScreen from './pages/auth/SignInScreen'
import EmailStartScreen from './pages/auth/EmailStartScreen'
import JoinInvitePage from './pages/auth/JoinInvitePage'
import Verify from './pages/auth/Verify'
import TwoFactorSetup from './pages/auth/TwoFactorSetup'
import TwoFactorMigrationGate from './components/TwoFactorMigrationGate'
import TwoFactorUpgradePage from './pages/auth/TwoFactorUpgradePage'
import ForgotPasswordScreen from './pages/auth/ForgotPasswordScreen'
import ResetPasswordVerify from './pages/auth/ResetPasswordVerify'
import NewPasswordScreen from './pages/auth/NewPasswordScreen'
// ★★★ THE E-MAIL LINK RESET. Beside the three SMS screens above, not instead of
//   them: both channels are live, and retiring the SMS one is a separate release.
import ForgotPasswordEmailScreen from './pages/auth/ForgotPasswordEmailScreen'
import ResetPasswordLinkScreen from './pages/auth/ResetPasswordLinkScreen'
import TwoFactorResetLinkScreen from './pages/auth/TwoFactorResetLinkScreen'

// v213.5 (C-A-OB-1): patient onboarding flow
import OnboardingFlow from './pages/onboarding/OnboardingFlow'
// PROD-1225: the re-consent screen (OnboardingAIConsent in mode="reconsent")
import AIConsentRenew from './pages/onboarding/AIConsentRenew'
import { aiConsentLaunch } from './services/aiConsentLaunch'
import { AI_CONSENT_EVENT, RECONSENT_PATH, createReconsentListener, safeReturnPath } from './utils/aiConsentSignal'

// Main screens
import HomeScreen from './pages/HomeScreen'
import FocusAreas from './pages/FocusAreas'
import SpecialtyBrowseScreen from './pages/SpecialtyBrowseScreen'
import Emergency from './pages/Emergency'
import CaseHome from './pages/CaseHome'

// Describe flow
import UrgentScreen from './pages/describe/UrgentScreen'
import DescribeVoice from './pages/describe/DescribeVoice'
import DescribeSection from './pages/describe/DescribeSection'
import DescribeReview from './pages/describe/DescribeReview'
import DescribeAdaptive from './pages/describe/DescribeAdaptive'
import CaseTimeline from './pages/describe/CaseTimeline'
import ViewSummary from './pages/describe/ViewSummary'

// Case screens
import MyMedicalNote from './pages/case/MyMedicalNote'
import AuthorizationsScreen from './pages/case/AuthorizationsScreen'
import { postLoginCapture, createLoadSignIn, POST_LOGIN_KEY } from './utils/postLoginTarget'
import CareAgreementScreen from './pages/case/CareAgreementScreen'

// Detect flow
import DetectScreen from './pages/detect/DetectScreen'

// Diagnose flow
import DiagnoseScreen from './pages/diagnose/DiagnoseScreen'
import ScheduleFollowupScreen from './pages/diagnose/ScheduleFollowupScreen'

// Deliver flow — v120.4: MedicationReview is now a modal in DeliverScreen
import DeliverScreen from './pages/deliver/DeliverScreen'
import FurtherTestingScreen from './pages/deliver/FurtherTestingScreen'
import InstructionsScreen from './pages/deliver/InstructionsScreen'

// Share flow
import ShareScreen from './pages/share/ShareScreen'
import ShareChoiceScreen from './pages/share/ShareChoiceScreen'
import ShareHubScreen from './pages/share/ShareHubScreen'
import AllowAccessScreen from './pages/case/AllowAccessScreen' // s119: the patient allows each opener of a share code
import FindDoctorScreen from './pages/share/FindDoctorScreen'
import SendReportScreen from './pages/share/SendReportScreen'
import ReferralRequestScreen from './pages/referral/ReferralRequestScreen' // s106 referral screens A3/A4
import ReferralUploadScreen from './pages/referral/ReferralUploadScreen' // s106 referral screens A7/A8

// Cases flow
import CasesListScreen from './pages/cases/CasesListScreen'

// Visits flow
import VisitsScreen from './pages/visits/VisitsScreen'

// Profile screens
import ProfileScreen from './pages/profile/ProfileScreen'
import ProfileIdentification from './pages/profile/ProfileIdentification'
import ProfileInsurance from './pages/profile/ProfileInsurance'
import MyInsurance from './pages/profile/MyInsurance'
import { MY_INSURANCE_SCREEN, MY_INSURANCE_PATH } from './config/insuranceLevel1'
import ProfileAllergies from './pages/profile/ProfileAllergies'
import ProfileMedications from './pages/profile/ProfileMedications'
import ProfileMedicalHistory from './pages/profile/ProfileMedicalHistory'
import ProfileFamilyHistory from './pages/profile/ProfileFamilyHistory'
import ProfileSocialLifestyle from './pages/profile/ProfileSocialLifestyle'
import ImportMedicalRecordsScreen from './pages/profile/ImportMedicalRecordsScreen'

// Settings screens
import SettingsScreen from './pages/settings/SettingsScreen'
import NotificationsScreen from './pages/settings/NotificationsScreen'
import NotificationPreferencesScreen from './pages/settings/NotificationPreferencesScreen'
import ChangePasswordScreen from './pages/settings/ChangePasswordScreen'
import SecurityScreen from './pages/settings/SecurityScreen'
import SessionPinScreen from './pages/settings/SessionPinScreen'
import TwoFactorManageScreen from './pages/settings/TwoFactorManageScreen'
import DeleteAccountScreen from './pages/settings/DeleteAccountScreen'
// TEST 3.01: change the account e-mail / mobile, verified on the NEW value with a code.
import ChangeEmailScreen from './pages/settings/ChangeEmailScreen'
import ChangePhoneScreen from './pages/settings/ChangePhoneScreen'
import AiDataConsentScreen from './pages/settings/AiDataConsentScreen'
import PrivacyPolicyScreen from './pages/settings/PrivacyPolicyScreen'
import TermsConditionsScreen from './pages/settings/TermsConditionsScreen'

// Doctors
import DoctorProfileScreen from './pages/doctors/DoctorProfileScreen'
import DoctorsScreen from './pages/doctors/DoctorsScreen'
import MyDoctorsScreen from './pages/doctors/MyDoctorsScreen'

// Landing page (web)
import PatientLandingPage from './pages/PatientLandingPage'
// v225.2.0 — public, no-login agreement sign page (SMS/email link)
import SignAgreementScreen from './pages/sign/SignAgreementScreen'
// PROD-1201: the dev scratch page is DEV ONLY. Vite replaces import.meta.env.DEV with a literal
// false in a production build, so this branch, the dynamic import and the page are dropped from it.
const StreamTestPage = import.meta.env.DEV ? lazy(() => import('./pages/StreamTestPage')) : null
import { recordCaseTrail } from './utils/caseBack'
import { REPORT_PROBLEM_ENABLED } from './config/featureFlags'
import { rememberReportRoute } from './utils/reportRouteMemory'
import { renewPushIfKeyChanged } from './services/push'
import { startNativePush } from './services/nativePush'

// v242.8.3: neutral full-screen hold shown while a protected route waits for the
// profile to resolve — prevents a Home flash before the onboarding gate decides.
// On-brand, calm, and brief (one profile load, once per session).
function ProfileGateHold() {
  // v242.12.0: neutral white hold matching Home (the common post-login
  // destination), with the branded loader — so an existing user logging in no
  // longer sees a pale-blue flash before Home paints. (Prior versions painted
  // the base #EAF3FB to match the onboarding carousel; Home is far more common.)
  return (
    <div className="screen-min-h bg-white flex items-center justify-center">
      <OrellaLoader message="" />
    </div>
  )
}

// ★ PROD-38 (patient half, P3): the route this page load started on, and whether anyone has
//   signed in during it. A protected route the tab was LOADED on without a session (a new tab,
//   a link from a message, the vercel.app alias forwarding here) is kept for after the login;
//   see utils/postLoginTarget.js.
// v242.9.0: validate a rehydrated (persisted) session against the server exactly once per app
//   boot (guards against a deleted account resurrecting a logged-in session from storage; a
//   fresh login doesn't need it, login validates server-side). _loadSignIn runs that check, and
//   (PROD-38 repair, N3) counts a sign-in only once the check has settled with the session still
//   good, so a hard load with an expired token keeps the asked-for route like a new tab does.
const _BOOT_PATH = (() => { try { return window.location.pathname + window.location.search } catch { return '' } })()
const _loadSignIn = createLoadSignIn()

// ★★★ v242.169.0 — TOKEN-KEY-1, THE HALF THAT DELETING THE WRITES DOES NOT DO.
//   Two screens wrote the live JWT a second time under 'orella_token' and no
//   logout removed it. Those writes are gone, which stops the leak on builds
//   from here on and does nothing at all for the devices that already have the
//   value sitting in localStorage. This runs once per boot, before anything is
//   rendered, and is a no-op on a device that never wrote it.
try { purgeLegacyAuth() } catch (_) {}

function ProtectedRoute({ children, allowOnboarding = false }) {
  const isAuthenticated = useUserStore((state) => state.isAuthenticated)
  const validateSession = useUserStore((state) => state.validateSession)
  const pendingUserId = useUserStore((state) => state.pendingUserId)
  const profile = useProfileStore((state) => state.profile)
  const loadProfile = useProfileStore((state) => state.loadProfile)
  const loading = useProfileStore((state) => state.loading)
  const _loc = useLocation()
  // PROD-1225: the launch check. Once per signed-in session (aiConsentLaunch caches per user),
  // and only once onboarding is done: onboarding has its own consent step, and the re-consent
  // route itself never asks, so the gate cannot loop. It never holds the
  // screen: the answer arrives while the route renders, and an error or `enforced: false`
  // answers 'ok' (the server gate stays the authority).
  const onReconsentRoute = _loc.pathname === RECONSENT_PATH
  const consentKey = useUserStore((state) => state.user?.id ?? null)
  const onboardingDone = profile?.onboardingLoaded === true && profile?.onboardingCompleted !== false
  const consentCheckOn = isAuthenticated && !allowOnboarding && !onReconsentRoute && onboardingDone
  const [launchGate, setLaunchGate] = useState(() => aiConsentLaunch.peek(consentKey))
  useEffect(() => {
    if (!consentCheckOn) return undefined
    let alive = true
    aiConsentLaunch.run(consentKey).then((r) => { if (alive) setLaunchGate(r) })
    return () => { alive = false }
  }, [consentCheckOn, consentKey])

  // v213.5 (C-A-OB-1): Trigger profile fetch on first mount of any
  // protected route so the onboarding gate has data to act on.
  useEffect(() => {
    if (isAuthenticated) {
      _loadSignIn.seen(validateSession, () => useUserStore.getState().isAuthenticated)
      // ★★★ v242.145.0 PROFILE-LOAD-ONESHOT-1 — retry on the DATA flag, not on
      //     the gate flag. `onboardingLoaded` is set on the error path too (it
      //     has to be, or the route gate hangs), so keying the retry on it meant
      //     a failed load could never be retried by the thing meant to retry it.
      if (!profile?.profileLoaded && !loading) loadProfile()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated])

  if (!isAuthenticated) {
    // v228.1.0 (Phase 1b): allow pre-auth entry to /onboarding ONLY, so the
    // 2FA step can run in-flow. At this point the patient carries only a
    // pendingUserId (set at phone verification); Basics is in-memory and the
    // 2FA step is what grants the live session. No PHI is read or written
    // before that. Every other protected route still bounces to /welcome,
    // and if the pendingUserId is gone (e.g. tab closed) this falls through
    // to sign-in, whose needs2faSetup path still reaches /security.
    if (allowOnboarding && pendingUserId) {
      return children
    }
    // v223.3.0 (F4): preserve a case deep link (e.g. from an SMS/email
    // notification tap while logged out) so sign-in can resume into it.
    // ★ PROD-38 (P3): and any other safe protected route this page load started on.
    // ★ PROD-687 (P3 repair 2): nothing at all once someone has signed in during this load and
    //   is now signed out — that route belongs to the person who left (see postLoginTarget.js).
    try {
      const keep = postLoginCapture(_loc, { firstLoad: !_loadSignIn.signedIn && (_loc.pathname + _loc.search) === _BOOT_PATH, afterSignOut: _loadSignIn.signedIn })
      if (keep) sessionStorage.setItem(POST_LOGIN_KEY, keep)
    } catch {}
    return <Navigate to="/welcome" replace />
  }

  // v242.8.3: never paint a protected screen (e.g. Home) before we know whether
  // the patient still needs onboarding — a fresh signup would otherwise flash
  // Home for a beat before the gate redirects to /onboarding. Hold neutral until
  // the profile resolves. loadProfile always sets onboardingLoaded (success /
  // empty / error), so this can't hang. Onboarding routes render immediately.
  if (!allowOnboarding && !profile?.onboardingLoaded) {
    return <ProfileGateHold />
  }

  // v213.5 (C-A-OB-1): If the profile load has resolved and the patient
  // has not completed onboarding, route them into /onboarding. The
  // /onboarding route itself sets allowOnboarding so it doesn't loop.
  if (!allowOnboarding && profile?.onboardingLoaded === true && profile?.onboardingCompleted === false) {
    return <Navigate to="/onboarding" replace />
  }

  // PROD-1225: the server enforces AI consent and this patient's is not current -> re-consent
  // first, then back here. Re-read from the shared check so a consent just recorded wins over
  // this route's own copy of the answer.
  if (consentCheckOn && launchGate === 'redirect' && aiConsentLaunch.peek(consentKey) === 'redirect') {
    return <Navigate to={RECONSENT_PATH} replace state={{ returnTo: safeReturnPath(_loc.pathname + _loc.search) }} />
  }

  return children
}

// v223.3.0 (F4): /case/:caseId loader. Notification deep links (SMS/email and
// the in-app bell) target /case/{id}?openChat=1, but there was no dynamic case
// route — the path fell through the catch-all to "/". This resolves the case by
// id into the store, then renders CaseHome (which honors ?openChat=1). Falls
// back to /cases for stale/foreign ids.
function CaseRoute() {
  const { caseId } = useParams()
  const routeLoc = useLocation() // s126: ?go=opened
  const currentCase = useCaseStore((s) => s.currentCase)
  const cases = useCaseStore((s) => s.cases)
  const setCurrentCase = useCaseStore((s) => s.setCurrentCase)
  const loadCases = useCaseStore((s) => s.loadCases)
  const [state, setState] = useState('resolving') // 'resolving' | 'ready' | 'notfound'

  useEffect(() => {
    let cancelled = false
    const resolve = async () => {
      if (currentCase?.id === caseId) { setState('ready'); return }
      let list = useCaseStore.getState().cases || cases || []
      if (list.length === 0) {
        try { await loadCases() } catch {}
        if (cancelled) return
        list = useCaseStore.getState().cases || []
      }
      const found = list.find((c) => c.id === caseId || c.caseId === caseId)
      if (cancelled) return
      if (found) { setCurrentCase(found); setState('ready') }
      else { setState('notfound') }
    }
    setState('resolving')
    resolve()
    return () => { cancelled = true }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [caseId])

  if (state === 'notfound') return <Navigate to="/cases" replace />
  // s126: the "Your case was opened" notice (?go=opened) lands on the share hub with the opener's sheet open.
  if (state === 'ready' && currentCase?.id === caseId && new URLSearchParams(routeLoc.search).get('go') === 'opened') {
    return <Navigate to="/case/share-hub" replace state={{ openedSheet: true }} />
  }
  if (state !== 'ready' || currentCase?.id !== caseId) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <OrellaSpinner size={28} />
        <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
      </div>
    )
  }
  return <CaseHome />
}


// Page wrapper with entrance animation
function PageWrapper({ children, direction }) {
  // v237.15.0: Canonical screen motion — top-down drop to a hard stop, with a
  // quick downward-drift exit (wired via AnimatePresence around <Routes>).
  // Lateral tab↔tab peers fade; reduced-motion users get opacity only.
  const reduce = useReducedMotion()
  const variants = reduce
    ? reducedVariants
    : direction === 'fade'
      ? fadeVariants
      : screenVariants

  return (
    <motion.div
      variants={variants}
      initial="initial"
      animate="animate"
      exit="exit"
      style={{ minHeight: '100%' }}
    >
      {children}
    </motion.div>
  )
}

export default function App() {
  const fetchQuestionnaires = useQuestionnaireStore((state) => state.fetchQuestionnaires)
  const { language } = useI18n()
  const wrapperRef = useRef(null)
  const location = useLocation()
  // PROD-654: every rendered entry's path under its history index, for the case back button.
  useEffect(() => { recordCaseTrail(location.pathname) }, [location])
  // REP-10 PA-3: the screen a report from Settings is about — its route TEMPLATE only, in
  //   memory (utils/reportRouteMemory.js). Flag off (featureFlags.js): nothing is recorded.
  useEffect(() => { if (REPORT_PROBLEM_ENABLED) rememberReportRoute(location.pathname) }, [location])
  const [direction, setDirection] = useState('fade')
  const prevPathRef = useRef(location.pathname)

  // PROD-1225: the server's AI consent gate refused a call (403 `ai_consent_required`;
  // services/api.js dispatches the event). Navigate to the re-consent screen ONCE, with the
  // path the patient was on; the listener ignores the event on that screen and in onboarding,
  // and re-arms only after the patient has left the screen (utils/aiConsentSignal.js).
  const navigate = useNavigate()
  const navigateRef = useRef(navigate)
  const pathRef = useRef(location.pathname + location.search)
  const reconsentRef = useRef(null)
  useEffect(() => {
    const listener = createReconsentListener({
      getPath: () => pathRef.current,
      navigate: (to, opts) => navigateRef.current(to, opts),
    })
    reconsentRef.current = listener
    const onRequired = () => { listener.handle() }
    window.addEventListener(AI_CONSENT_EVENT, onRequired)
    return () => window.removeEventListener(AI_CONSENT_EVENT, onRequired)
  }, [])
  useEffect(() => {
    navigateRef.current = navigate
    pathRef.current = location.pathname + location.search
    reconsentRef.current?.onPath(location.pathname)
  }, [location, navigate])
  
  // === TDZ CRASH TRACER (v116.10.1) ===
  useEffect(() => {
    const handler = (event) => {
      if (event.error?.message?.includes('before initialization') || 
          event.error?.name === 'ReferenceError') {
        console.error('═══════════════════════════════════════════')
        console.error('🔴 GLOBAL: TDZ / ReferenceError caught')
        console.error('═══════════════════════════════════════════')
        console.error('Message:', event.error?.message)
        console.error('Stack:', event.error?.stack)
        console.error('Current route has path:', window.location.pathname.length > 1)
        console.error('Event has filename:', !!event.filename, 'has position:', !!event.lineno)
        console.error('═══════════════════════════════════════════')
      }
    }
    window.addEventListener('error', handler)
    return () => window.removeEventListener('error', handler)
  }, [])
  
  // Set the global ref
  useEffect(() => {
    scrollContainerRef.current = wrapperRef.current
  }, [])
  
  // v129.0.0: Fetch questionnaires from API with current language.
  // Re-fetches when language changes so translated questions are served.
  useEffect(() => {
    fetchQuestionnaires(language)
  }, [language])
  
  // v163.6.0: Start/stop notification polling based on auth state
  const isAuthenticated = useUserStore((s) => s.isAuthenticated)
  useEffect(() => {
    if (isAuthenticated) {
      useNotificationStore.getState().startPolling()
    } else {
      useNotificationStore.getState().stopPolling()
      useNotificationStore.getState().clearUnread()
    }
    return () => useNotificationStore.getState().stopPolling()
  }, [isAuthenticated])
  // PROD-637: once per signed-in session, a web-push subscription made with another VAPID key is
  //   renewed without a prompt (services/push.js; permission already granted, a subscription exists).
  useEffect(() => {
    if (isAuthenticated) renewPushIfKeyChanged().catch(() => {})
  }, [isAuthenticated])
  // s119 (D532): on the iPhone, once per signed-in session: ask iOS for permission (its prompt appears once),
  //   register with APNs and send the token to the API. A tapped push opens its notice (services/nativePush.js).
  const navigateForPush = useNavigate()
  useEffect(() => {
    if (isAuthenticated) startNativePush((path) => navigateForPush(path)).catch(() => {})
  }, [isAuthenticated, navigateForPush])
  // PROD-1225: a sign-out ends the session the launch check was cached for.
  useEffect(() => {
    if (!isAuthenticated) aiConsentLaunch.reset()
  }, [isAuthenticated])
  // PROD-1225 (review L3): a sign-in or sign-out re-arms the re-consent listener.
  useEffect(() => {
    reconsentRef.current?.onAuthChange()
  }, [isAuthenticated])
  
  // Determine navigation direction and scroll to top
  useEffect(() => {
    const prevPath = prevPathRef.current
    const nextPath = location.pathname
    
    if (prevPath !== nextPath) {
      setDirection(getNavigationDirection(prevPath, nextPath))
      prevPathRef.current = nextPath
      
      // Scroll to top
      // v200.0: Comprehensive scroll-to-top on every page transition
      // Both window AND scroll-wrapper must be reset — mobile browsers
      // can scroll the window independently from the wrapper div
      window.scrollTo(0, 0)
      if (wrapperRef.current) {
        wrapperRef.current.scrollTop = 0
      }
      // Belt-and-suspenders: also target .scroll-wrapper by query in case ref is stale
      const sw = document.querySelector('.scroll-wrapper')
      if (sw && sw !== wrapperRef.current) sw.scrollTop = 0
    }
  }, [location.pathname])
  
  return (
    <div ref={wrapperRef} className="scroll-wrapper">
      <ErrorBoundary>
      <GlobalAgentProvider>
      <AnimatePresence mode="wait" initial={false}>
      <Routes location={location} key={location.pathname}>
        {/* Landing page - default entry point */}
        <Route path="/" element={Capacitor.isNativePlatform() ? <Navigate to="/welcome" replace /> : <PageWrapper direction={direction}><PatientLandingPage /></PageWrapper>} />
        <Route path="/landing" element={<PageWrapper direction={direction}><PatientLandingPage /></PageWrapper>} />

        {/* v225.2.0 — public no-login agreement signing (SMS/email link) */}
        <Route path="/sign-agreement/:token" element={<PageWrapper direction={direction}><SignAgreementScreen /></PageWrapper>} />

        {/* v242.52.0 — Session D GATE D3 scratch page. DEV ONLY, no layout,
            no PageWrapper. Requires an active session (the mint endpoint is
            @auth_required). Remove at 4.6. */}
        {/* PROD-1197: every signed-in route sits under SessionLockGuard (see that file). */}
        {import.meta.env.DEV && (
          <Route element={<SessionLockGuard />}>
            <Route path="/dev/stream" element={<Suspense fallback={null}><StreamTestPage /></Suspense>} />
          </Route>
        )}
        
        {/* Auth flow - centered container, no tab bar */}
        <Route element={<AuthLayout />}>
          <Route path="/splash" element={<PageWrapper direction={direction}><WelcomeScreen /></PageWrapper>} />
          <Route path="/welcome" element={<PageWrapper direction={direction}><WelcomeScreen /></PageWrapper>} />
          <Route path="/signup" element={<PageWrapper direction={direction}><SignUpScreen /></PageWrapper>} />
          <Route path="/signin" element={<PageWrapper direction={direction}><SignInScreen /></PageWrapper>} />
          <Route path="/email" element={<PageWrapper direction={direction}><EmailStartScreen /></PageWrapper>} />
          <Route path="/verify" element={<PageWrapper direction={direction}><Verify /></PageWrapper>} />
          {/* PROD-1197: both run for the SIGNED-IN account (2FA change / upgrade): locked -> lock screen. */}
          <Route element={<SessionLockGuard />}>
            <Route path="/security" element={<PageWrapper direction={direction}><TwoFactorSetup /></PageWrapper>} />
            <Route path="/2fa-upgrade" element={<PageWrapper direction={direction}><TwoFactorUpgradePage /></PageWrapper>} />
          </Route>
          <Route path="/forgot-password" element={<PageWrapper direction={direction}><ForgotPasswordScreen /></PageWrapper>} />
          <Route path="/forgot-password/verify" element={<PageWrapper direction={direction}><ResetPasswordVerify /></PageWrapper>} />
          <Route path="/forgot-password/new-password" element={<PageWrapper direction={direction}><NewPasswordScreen /></PageWrapper>} />
          {/* ★★★ THE E-MAIL CHANNEL. /reset-password IS THE PATH orella-api MAILS OUT —
              PATIENT_PASSWORD_RESET_APP_PATH. It must match that constant BYTE FOR BYTE
              or every link in every reset mail 404s and no token is ever spent, with
              nothing on either side going red. scripts/check-patient-email-reset.mjs
              reads the constant out of the API repo's git blob and compares it. */}
          <Route path="/forgot-password/email" element={<PageWrapper direction={direction}><ForgotPasswordEmailScreen /></PageWrapper>} />
          <Route path="/reset-password" element={<PageWrapper direction={direction}><ResetPasswordLinkScreen /></PageWrapper>} />
          {/* PROD-1271: /two-factor-reset IS THE PATH orella-api MAILS OUT for the 2FA reset link
              (PATIENT_2FA_EMAIL_RESET_APP_PATH). Public, like /reset-password: under AuthLayout only,
              never under SessionLockGuard, ProtectedRoute or the onboarding gate (the person holding
              the link is signed out). scripts/check-2fa-email-reset.mjs holds both. */}
          <Route path="/two-factor-reset" element={<PageWrapper direction={direction}><TwoFactorResetLinkScreen /></PageWrapper>} />
          <Route path="/join" element={<PageWrapper direction={direction}><JoinInvitePage /></PageWrapper>} />
          <Route path="/join/:inviteCode" element={<PageWrapper direction={direction}><JoinInvitePage /></PageWrapper>} />
        </Route>

        {/* v213.5 (C-A-OB-1): Patient onboarding — full-screen, no layout chrome.
            allowOnboarding=true so the gate doesn't loop redirect to itself. */}
        {/* PROD-1197: outside MobileLayout, so the lock screen renders INSTEAD of the run. */}
        <Route element={<SessionLockGuard />}>
        <Route
          path="/onboarding"
          element={
            <ProtectedRoute allowOnboarding={true}>
              <OnboardingFlow />
            </ProtectedRoute>
          }
        />
        {/* PROD-1225: re-consent, full-screen like onboarding (RECONSENT_PATH in utils/aiConsentSignal.js).
            ProtectedRoute's launch check skips this path, so it never redirects the route to itself. */}
        <Route
          path="/ai-consent/renew"
          element={
            <ProtectedRoute>
              <AIConsentRenew />
            </ProtectedRoute>
          }
        />
        </Route>
        
        {/* Main app with tab bar */}
        {/* PROD-1197: keepScreen — the screen stays mounted under the lock overlay, as before. */}
        <Route element={<SessionLockGuard keepScreen />}>
        <Route element={<MobileLayout />}>
          <Route path="/home" element={<ProtectedRoute><PageWrapper direction={direction}><HomeScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/specialties" element={<ProtectedRoute><PageWrapper direction={direction}><SpecialtyBrowseScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/specialty/:specialtyId" element={<ProtectedRoute><PageWrapper direction={direction}><FocusAreas /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/emergency" element={<ProtectedRoute><PageWrapper direction={direction}><Emergency /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/home" element={<ProtectedRoute><PageWrapper direction={direction}><CaseHome /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/urgent" element={<ProtectedRoute><PageWrapper direction={direction}><UrgentScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/voice" element={<ProtectedRoute><PageWrapper direction={direction}><DescribeVoice /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/details" element={<ProtectedRoute><PageWrapper direction={direction}><DescribeSection /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/signs-symptoms" element={<ProtectedRoute><PageWrapper direction={direction}><DescribeSection /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/context" element={<ProtectedRoute><PageWrapper direction={direction}><DescribeSection /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/additional" element={<ProtectedRoute><PageWrapper direction={direction}><DescribeSection /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/adaptive" element={<ProtectedRoute><PageWrapper direction={direction}><DescribeAdaptive /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/review" element={<ProtectedRoute><PageWrapper direction={direction}><DescribeReview /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/summary" element={<ProtectedRoute><PageWrapper direction={direction}><ViewSummary /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/conditions" element={<ProtectedRoute><PageWrapper direction={direction}><ViewSummary view="conditions" /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/describe/warnings" element={<ProtectedRoute><PageWrapper direction={direction}><ViewSummary view="warnings" /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/encounters" element={<ProtectedRoute><PageWrapper direction={direction}><CaseTimeline /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/note" element={<ProtectedRoute><PageWrapper direction={direction}><MyMedicalNote /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/authorizations" element={<ProtectedRoute><PageWrapper direction={direction}><AuthorizationsScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/agreement" element={<ProtectedRoute><PageWrapper direction={direction}><CareAgreementScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/detect" element={<ProtectedRoute><PageWrapper direction={direction}><DetectScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/diagnose" element={<ProtectedRoute><PageWrapper direction={direction}><DiagnoseScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/schedule-followup" element={<ProtectedRoute><PageWrapper direction={direction}><ScheduleFollowupScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/deliver" element={<ProtectedRoute><PageWrapper direction={direction}><DeliverScreen /></PageWrapper></ProtectedRoute>} />
          {/* v120.4: medication-survey removed — now a modal in DeliverScreen. Redirect for backward compat */}
          <Route path="/case/deliver/medication-survey" element={<ProtectedRoute><PageWrapper direction={direction}><DeliverScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/deliver/further-testing" element={<ProtectedRoute><PageWrapper direction={direction}><FurtherTestingScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/deliver/instructions" element={<ProtectedRoute><PageWrapper direction={direction}><InstructionsScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/share-choice" element={<ProtectedRoute><PageWrapper direction={direction}><ShareChoiceScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/share" element={<ProtectedRoute><PageWrapper direction={direction}><ShareScreen /></PageWrapper></ProtectedRoute>} />
          {/* s98 Phases 4 + 6 + 7 (D468): the share hub, the US doctor finder and the fax send. */}
          <Route path="/case/share-hub" element={<ProtectedRoute><PageWrapper direction={direction}><ShareHubScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/find-doctor" element={<ProtectedRoute><PageWrapper direction={direction}><FindDoctorScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/send-report" element={<ProtectedRoute><PageWrapper direction={direction}><SendReportScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/referral-request" element={<ProtectedRoute><PageWrapper direction={direction}><ReferralRequestScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/case/referral-upload" element={<ProtectedRoute><PageWrapper direction={direction}><ReferralUploadScreen /></PageWrapper></ProtectedRoute>} />
          {/* s119: "Someone wants to open your case" — the patient's Allow / Deny (the notice's deep link) */}
          <Route path="/case/:caseId/allow-access" element={<ProtectedRoute><PageWrapper direction={direction}><AllowAccessScreen /></PageWrapper></ProtectedRoute>} />
          {/* v223.3.0 (F4): dynamic case deep link — must come after all static /case/* routes */}
          <Route path="/case/:caseId" element={<ProtectedRoute><PageWrapper direction={direction}><CaseRoute /></PageWrapper></ProtectedRoute>} />
          <Route path="/cases" element={<ProtectedRoute><PageWrapper direction={direction}><CasesListScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/visits" element={<ProtectedRoute><PageWrapper direction={direction}><VisitsScreen /></PageWrapper></ProtectedRoute>} />
          
          {/* Profile Routes */}
          <Route path="/profile" element={<ProtectedRoute><PageWrapper direction={direction}><ProfileScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/profile/identification" element={<ProtectedRoute><PageWrapper direction={direction}><ProfileIdentification /></PageWrapper></ProtectedRoute>} />
          <Route path="/profile/insurance" element={<ProtectedRoute><PageWrapper direction={direction}><ProfileInsurance /></PageWrapper></ProtectedRoute>} />
          {/* PROD-581: "My insurance", registered only while src/config/insuranceLevel1.js is on (OFF) */}
          {MY_INSURANCE_SCREEN && <Route path={MY_INSURANCE_PATH} element={<ProtectedRoute><PageWrapper direction={direction}><MyInsurance /></PageWrapper></ProtectedRoute>} />}
          <Route path="/profile/allergies" element={<ProtectedRoute><PageWrapper direction={direction}><ProfileAllergies /></PageWrapper></ProtectedRoute>} />
          <Route path="/profile/medications" element={<ProtectedRoute><PageWrapper direction={direction}><ProfileMedications /></PageWrapper></ProtectedRoute>} />
          <Route path="/profile/medical-history" element={<ProtectedRoute><PageWrapper direction={direction}><ProfileMedicalHistory /></PageWrapper></ProtectedRoute>} />
          <Route path="/profile/family-history" element={<ProtectedRoute><PageWrapper direction={direction}><ProfileFamilyHistory /></PageWrapper></ProtectedRoute>} />
          <Route path="/profile/social-lifestyle" element={<ProtectedRoute><PageWrapper direction={direction}><ProfileSocialLifestyle /></PageWrapper></ProtectedRoute>} />
          <Route path="/profile/import-records" element={<ProtectedRoute><PageWrapper direction={direction}><ImportMedicalRecordsScreen /></PageWrapper></ProtectedRoute>} />
          
          {/* Settings Routes */}
          <Route path="/settings" element={<ProtectedRoute><PageWrapper direction={direction}><SettingsScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/notifications" element={<ProtectedRoute><PageWrapper direction={direction}><NotificationsScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/notification-preferences" element={<ProtectedRoute><PageWrapper direction={direction}><NotificationPreferencesScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/settings/change-password" element={<ProtectedRoute><PageWrapper direction={direction}><ChangePasswordScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/settings/two-factor" element={<ProtectedRoute><PageWrapper direction={direction}><TwoFactorManageScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/settings/security" element={<ProtectedRoute><PageWrapper direction={direction}><SecurityScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/settings/session-pin" element={<ProtectedRoute><PageWrapper direction={direction}><SessionPinScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/settings/delete-account" element={<ProtectedRoute><PageWrapper direction={direction}><DeleteAccountScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/settings/email" element={<ProtectedRoute><PageWrapper direction={direction}><ChangeEmailScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/settings/phone" element={<ProtectedRoute><PageWrapper direction={direction}><ChangePhoneScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/settings/ai-consent" element={<ProtectedRoute><PageWrapper direction={direction}><AiDataConsentScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/doctors" element={<ProtectedRoute><PageWrapper direction={direction}><DoctorsScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/doctors/mine" element={<ProtectedRoute><PageWrapper direction={direction}><MyDoctorsScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/doctor/:doctorId" element={<ProtectedRoute><PageWrapper direction={direction}><DoctorProfileScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/settings/privacy-policy" element={<ProtectedRoute><PageWrapper direction={direction}><PrivacyPolicyScreen /></PageWrapper></ProtectedRoute>} />
          <Route path="/settings/terms" element={<ProtectedRoute><PageWrapper direction={direction}><TermsConditionsScreen /></PageWrapper></ProtectedRoute>} />
        </Route>
        </Route>
        
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
      </AnimatePresence>
      </GlobalAgentProvider>
      </ErrorBoundary>
    </div>
  )
}
